import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { companyInfo } from '../utils/mockData';
import { SubscribeForm } from './SubscribeForm';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* CTA Banner */}
      <div className="bg-brand-blue text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">Ready to Compare Coverage Options?</h3>
          <p className="text-base mb-4">Let us help you find the right insurance for your needs and budget.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#quote" className="inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-8 py-3 rounded-md font-semibold hover:bg-brand-orange-light transition" data-testid="footer-quote-btn">
              Get My Free Quote
            </Link>
            <a href={`tel:${companyInfo.phoneRaw}`} className="inline-flex items-center gap-2 bg-white text-brand-blue px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition" data-testid="footer-call-btn">
              <Phone size={20} />
              {companyInfo.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">Our Company</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="hover:text-brand-orange transition">About Us</Link></li>
              <li><Link to="/testimonials" className="hover:text-brand-orange transition">Testimonials</Link></li>
              <li><Link to="/faq" className="hover:text-brand-orange transition">FAQ</Link></li>
              <li><Link to="/careers" className="hover:text-brand-orange transition">Agent Careers</Link></li>
              <li><Link to="/contact" className="hover:text-brand-orange transition">Contact Us</Link></li>
            </ul>
          </div>

          {/* Coverage Options */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">Coverage Options</h4>
            <ul className="space-y-2">
              <li><Link to="/life-insurance" className="hover:text-brand-orange transition">Life Insurance</Link></li>
              <li><Link to="/final-expense" className="hover:text-brand-orange transition">Final Expense</Link></li>
              <li><Link to="/health-supplemental" className="hover:text-brand-orange transition">Health & Supplemental</Link></li>
              <li><Link to="/medicare" className="hover:text-brand-orange transition">Medicare</Link></li>
              <li><Link to="/iuls" className="hover:text-brand-orange transition">IULs</Link></li>
              <li><Link to="/annuities" className="hover:text-brand-orange transition">Annuities</Link></li>
              <li><Link to="/dental" className="hover:text-brand-orange transition">Dental</Link></li>
              <li><Link to="/vision" className="hover:text-brand-orange transition">Vision</Link></li>
            </ul>
          </div>

          {/* Get In Touch */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">Get In Touch</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin size={20} className="text-brand-orange mt-1 flex-shrink-0" />
                <div>
                  <span className="block text-white text-sm font-semibold">Mailing Address</span>
                  <span>{companyInfo.name}</span>
                  <span className="block">{companyInfo.address}</span>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={20} className="text-brand-orange flex-shrink-0" />
                <a href={`tel:${companyInfo.phoneRaw}`} className="hover:text-brand-orange transition">{companyInfo.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={20} className="text-brand-orange flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="hover:text-brand-orange transition">{companyInfo.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={20} className="text-brand-orange mt-1 flex-shrink-0" />
                <span>{companyInfo.hours}</span>
              </li>
            </ul>
          </div>

          {/* Stay Connected */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">Stay Connected</h4>
            <p className="text-sm mb-4">Get insurance tips and updates delivered to your inbox.</p>
            <SubscribeForm />
            <div className="flex gap-2">
              <a href={companyInfo.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="bg-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-brand-orange transition">
                <Facebook size={20} />
              </a>
              <a href={companyInfo.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="bg-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-brand-orange transition">
                <Twitter size={20} />
              </a>
              <a href={companyInfo.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="bg-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-brand-orange transition">
                <Linkedin size={20} />
              </a>
              <a href={companyInfo.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="bg-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-brand-orange transition">
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Medicare Compliance */}
      <div className="border-t border-gray-700 py-6">
        <div className="container mx-auto px-4">
          <p className="text-xs text-gray-400 text-center mb-2">
            Purposefully Covered Insurance Group is owned and operated by David Ashley, a licensed and insured insurance agent. We are not affiliated with or endorsed by the U.S. government or Medicare.
          </p>
          <p className="text-xs text-gray-400 text-center">
            Licensed Insurance Agent | Georgia License #: [To be provided]
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} Purposefully Covered Insurance Group LLC. All Rights Reserved.
            </p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-brand-orange transition">Privacy Policy</Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-brand-orange transition">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
